import type { ConversationNotice } from "../composables/chatTimeline";
import type { DeliveryState } from "../composables/messageDelivery";
import type { MessagePricing } from "../composables/metaPricing";
import type { ProfileTone } from "../composables/profileColors";
export interface MessageReaction {
  userId: string;
  emoji: string;
}
/** What a link preview shows: the page's title, description, site and picture. */
export interface LinkPreview {
  url: string;
  title: string;
  description: string | null;
  site: string;
  /** API path of the stored picture, or null. */
  imageUrl: string | null;
}

export interface Message {
  /** A link sent with its preview, as WhatsApp draws it above the text. */
  linkPreview?: LinkPreview;
  /**
   * What a template or an interactive message showed around its text, as WhatsApp draws it:
   * the header in bold, the footer in grey and the buttons under the bubble.
   */
  rich?: {
    header?: string | null;
    footer?: string | null;
    buttons?: Array<{ type: string; text: string }>;
    /** A list message's rows, as the contact sees them after tapping its button. */
    list?: {
      title: string;
      options: Array<{
        id?: string;
        title: string;
        description?: string | null;
      }>;
      /** The row the contact chose, once they answered. */
      chosen?: { id: string | null; title: string };
    };
  };
  id: string;
  text: string;
  out: boolean;
  time: string;
  date?: string;
  kind?:
    | "text"
    | "image"
    | "video"
    | "contact"
    | "file"
    | "audio"
    | "sticker"
    | "poll"
    | "event";
  src?: string;
  /** Provider id (WhatsApp wamid) when the message went through the channel; needed to quote it. */
  providerMessageId?: string;
  name?: string;
  size?: string;
  duration?: string;
  reply?: string;
  replyAuthor?: string;
  anonymous?: boolean;
  expiresAt?: string;
  reactions?: MessageReaction[];
  attending?: boolean;
  starred?: boolean;
  pinned?: boolean;
  deleted?: boolean;
  /** Removed by someone of the team: who and when. Only people allowed to see removed messages get these. */
  removed?: string;
  edited?: boolean;
  status?: DeliveryState;
  /** Delivery failure detail from the channel; shown under the bubble. */
  failed?: string;
  /** Accepted by us, not yet by the channel. */
  queued?: boolean;
  options?: string[];
  votes?: number[];
  selected?: number[];
  multiple?: boolean;
  /** Real media (API mode). */
  mime?: string;
  voice?: boolean;
  pages?: number;
  width?: number;
  height?: number;
  durationMs?: number;
  /** A received audio's text (decision 0038); "pending" while it is being transcribed. */
  transcript?: string;
  transcriptStatus?: "pending" | "done" | "failed";
  /** Same file with Content-Disposition: attachment. */
  downloadUrl?: string;
  /** Initials on the voice-note avatar. */
  initials?: string;
  /** Upload progress 0..1 while a local file is on its way; undefined once it is a server message. */
  uploading?: number;
  /** Key of the local outgoing entry, for retry and discard. */
  outgoingKey?: string;
  /** Contacts shared in the message. */
  contacts?: Array<{ name: string; phones: string[] }>;
  /** Creation time in epoch milliseconds (API mode); groups photos into albums. */
  ts?: number;
  /** Receipt times from WhatsApp (ISO), shown in "Dados da mensagem". */
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  /** First play of a voice note (blue microphone). */
  playedAt?: string;
  /** Sent from the WhatsApp Business app on the phone (a number in coexistence). */
  fromPhone?: boolean;
  /** Written by an assistant (its reply trace can be asked). */
  byAssistant?: boolean;
  /** Brought by an import (decision 0024): the platform, and who sent it there when they have no member here. */
  importedFrom?: string;
  authorName?: string;
  /** How Meta billed an outgoing message, for "Dados da mensagem". */
  pricing?: MessagePricing;
  /** Meta (or DoneTalk) error code of a failed send. */
  errorCode?: string;
  /** Raw failure detail from Meta, for "Dados da mensagem". */
  errorDetail?: string;
  /** A failed server message that "Tentar de novo" can send again. */
  canRetry?: boolean;
  /** WhatsApp id of the quoted message (API mode), resolved into `reply` by the inbox. */
  replyToProvider?: string;
  /** A contact's answer to a list or buttons message: the option they chose. */
  chosen?: { id: string | null; title: string };
  /** The quoted message is ours ("Você", green bar) rather than the contact's (blue bar). */
  replyOut?: boolean;
  /** Id of the quoted message in the timeline, to scroll to it. */
  replyId?: string;
  /** Picture of a quoted photo or video. */
  replyThumb?: string;
}
export interface Chat {
  id: string;
  name: string;
  preview: string;
  time: string;
  unread: number;
  group?: boolean;
  pinned?: boolean;
  pinnedAt?: number;
  favorite?: boolean;
  archived?: boolean;
  muted?: boolean;
  lists?: string[];
  blocked?: boolean;
  reported?: boolean;
  members?: string[];
  notices?: ConversationNotice[];
  color?: string;
  tone?: ProfileTone;
  messages: Message[];
}
export const seedMessages: Message[] = [
  {
    id: "m1",
    text: "Oi! Separei algumas referências para a nossa próxima conversa.",
    out: false,
    time: "10:24",
    status: "read",
  },
  {
    id: "m2",
    text: "Boa! Pode mandar por aqui 😊",
    out: true,
    time: "10:25",
    status: "read",
  },
  {
    id: "m3",
    text: "Um lugar para desacelerar. 🌿",
    out: false,
    time: "10:26",
    kind: "image",
    src: "/assets/landscape.svg",
  },
  {
    id: "m4",
    text: "Gostei muito das cores e da luz. Vamos seguir por esse caminho.",
    out: true,
    time: "10:28",
    status: "read",
    reply: "Um lugar para desacelerar. 🌿",
    replyAuthor: "Clara Martins",
    replyThumb: "/assets/landscape.svg",
    reactions: [{ userId: "clara", emoji: "💚" }],
  },
  {
    id: "m5",
    text: "Referências do projeto",
    out: false,
    time: "10:29",
    kind: "file",
    name: "Referências do projeto.txt",
    size: "2 KB",
    src: "/assets/demo.txt",
  },
  {
    id: "m6",
    text: "Mensagem de voz",
    out: false,
    time: "10:31",
    kind: "audio",
    src: "/assets/demo.wav",
    duration: "0:04",
  },
  {
    id: "m7",
    text: "Qual horário funciona melhor?",
    out: false,
    time: "10:32",
    kind: "poll",
    options: ["Amanhã, às 10h", "Amanhã, às 14h", "Quinta, às 9h"],
    votes: [2, 4, 1],
    selected: [],
  },
  {
    id: "m8",
    text: "Amanhã às 14h fica perfeito. Vou deixar o material organizado até lá.",
    out: true,
    time: "10:34",
    status: "read",
  },
  {
    id: "m9",
    text: "Fechado! Obrigada 💚",
    out: false,
    time: "10:35",
    reactions: [{ userId: "clara", emoji: "👍" }],
  },
  {
    id: "m10",
    text: "Combinado. Até amanhã!",
    out: true,
    time: "10:36",
    status: "delivered",
  },
];
const people = [
  ["Clara Martins", "Combinado. Até amanhã!", "#f1dccf"],
  ["Estúdio Horizonte", "Rafa: a apresentação ficou ótima", "#dce8dd"],
  ["Pedro Almeida", "Vou conferir e te retorno.", "#dce4f5"],
  ["Júlia Costa", "Foto", "#f5dce5"],
  ["Equipe de produto", "Maya: temos novidades por aqui", "#e3dcf5"],
  ["Marina Lopes", "Obrigada! 💚", "#f5eadc"],
  ["Rafael Santos", "Mensagem de voz", "#dcebf1"],
  ["Café de sábado", "Lia: amanhã, às 9h?", "#eadfcc"],
  ["Lucas Ferreira", "Documento", "#e4e8f5"],
  ["Beatriz Oliveira", "Até mais!", "#f4d9d1"],
  ["Design & ideias", "Você: vamos nessa!", "#d9ece3"],
  ["Ana Ribeiro", "Amei a referência.", "#f0d9e8"],
  ["Gabriel Lima", "Tudo certo por aqui.", "#dae8ee"],
  ["Sofia Mendes", "Pode deixar 😊", "#e5e6c9"],
  ["Apenas Leo", "Seu espaço para ideias e anotações", "#fce2d7"],
];
export function createChats(): Chat[] {
  return people.map((p, i) => ({
    id: "c" + i,
    name: p[0],
    preview: p[1],
    color: p[2],
    time:
      i < 7
        ? ["10:36", "10:32", "09:58", "09:41", "09:15", "08:46", "08:02"][i]
        : "Ontem",
    unread: [0, 3, 1, 0, 12, 0, 0, 2][i] || 0,
    pinned: i < 2,
    favorite: i === 0 || i === 5,
    group: [1, 4, 7, 10, 14].includes(i),
    notices:
      i === 1
        ? [
            {
              id: "group-created",
              variant: "system",
              text: "Você criou o grupo “Estúdio Horizonte”.",
              icon: "communities",
              date: "2026-09-20",
            },
          ]
        : [],
    messages:
      i === 0
        ? structuredClone(seedMessages).map((message) => ({
            ...message,
            date: "2026-09-20",
          }))
        : [
            {
              id: "intro" + i,
              text:
                i === 14
                  ? "Um espaço só seu. Salve ideias, arquivos e lembretes aqui."
                  : p[1],
              time: "09:00",
              date: i < 7 ? "2026-09-20" : "2026-09-19",
              out: false,
            },
          ],
  }));
}
